"use client";

/**
 * LiquidSwitch
 * ────────────
 * Composite del design language v2 — mismo patrón que LiquidPillDropdown:
 *   · Track = LiquidSurface (carril, recibe el click del switch)
 *   · Thumb = LiquidPill circle (reactive, hoverDeform, hereda LiquidSurface)
 *
 * No se parchea LiquidSurface: el dot solo compone LiquidPill con las props
 * canónicas de reactividad, igual que el trigger de un dropdown.
 */

import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as React from "react";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import { LiquidPill } from "./liquid-pill";
import { LiquidSurface } from "./liquid-surface";

const TRACK_W = 52;
const TRACK_H = 28;
const TRACK_PADDING = 3;
/** Recorrido del thumb (track interno − diámetro circle xxs = 20px). */
const THUMB_TRAVEL_PX = TRACK_W - TRACK_PADDING * 2 - 20;

export interface LiquidSwitchProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    "className" | "style" | "asChild"
  > {
  variant?: CircleActionVariant;
  "aria-label"?: string;
}

export const LiquidSwitch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  LiquidSwitchProps
>(function LiquidSwitch(
  {
    variant = "indigo",
    checked,
    defaultChecked,
    onCheckedChange,
    disabled,
    id,
    "aria-label": ariaLabel,
    ...props
  },
  ref
) {
  const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
    defaultChecked ?? false
  );
  const isChecked = checked ?? uncontrolledChecked;

  const handleCheckedChange = (next: boolean) => {
    if (checked === undefined) {
      setUncontrolledChecked(next);
    }
    onCheckedChange?.(next);
  };

  return (
    <SwitchPrimitive.Root
      ref={ref}
      id={id}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      asChild
      {...props}
    >
      <LiquidSurface
        as="button"
        type="button"
        role="switch"
        variant={isChecked ? variant : "slate"}
        tone="tinted"
        shape="pill"
        size="free"
        interactive
        hoverBounce={false}
        hoverDeform={false}
        shadow={isChecked ? "default" : "neutral"}
        bodyStyle="flat"
        edgeStyle="default"
        surfaceStyle={isChecked ? "switchTrackActive" : "switchTrackRecessed"}
        cursorGlow={false}
        disabled={disabled}
        aria-label={ariaLabel}
        className="liquid-switch inline-flex shrink-0 items-center"
        style={{
          width: TRACK_W,
          height: TRACK_H,
          minWidth: TRACK_W,
          minHeight: TRACK_H,
          padding: TRACK_PADDING,
        }}
      >
        <SwitchPrimitive.Thumb asChild>
          <span
            className={cn(
              "relative z-[4] block h-5 w-5 transition-transform duration-300 ease-spring",
              "data-[state=checked]:translate-x-[var(--liquid-switch-travel)]",
              "data-[state=unchecked]:translate-x-0"
            )}
            style={{ ["--liquid-switch-travel" as string]: `${THUMB_TRAVEL_PX}px` }}
          >
            <LiquidPill
              as="span"
              shape="circle"
              size="xxs"
              variant={isChecked ? variant : "slate"}
              appearance={isChecked ? "normal" : "tinted"}
              reactive
              interactive
              hoverDeform
              hoverBounce
              deformAxis="both"
              bounceIntensity="subtle"
              shadow="floating"
            />
          </span>
        </SwitchPrimitive.Thumb>
      </LiquidSurface>
    </SwitchPrimitive.Root>
  );
});

LiquidSwitch.displayName = "LiquidSwitch";
